import { green, grey } from "@mui/material/colors";
import { ModuleRegistry } from "ag-grid-community";
import {
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  MultiFilterModule,
  SetFilterModule,
  SideBarModule,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { forwardRef, useCallback, useMemo } from "react";

import { agApiSizeColumnsToFit } from "../../../../../_src_shared/utils/agGrid/api/agApiSizeColumnsToFit";
import { columnTypesDollar } from "../../../../../_src_shared/utils/agGrid/columnTypes/columnTypesDollar";
// ✅ use your shared formatter (update path if needed)

import AgGridTableWrapper from "../../../../../components/agGrid/AgGridTableWrapper";
import { formatDollar } from "../../../../../utils/numbers/formatDollar";
import { isEmpty } from "lodash";

ModuleRegistry.registerModules([
  SideBarModule,
  MultiFilterModule,
  FiltersToolPanelModule,
  ColumnsToolPanelModule,
  SetFilterModule,
]);

const formatDateShort = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString();
};

const formatFrequency = (freq) => {
  if (!freq) return "";
  const s = String(freq).toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const EscrowItemsTable = forwardRef(
  (
    { rowData, onRowClicked, loading, quickFilterText, showTotals = true },
    ref
  ) => {
    const columnDefs = useMemo(() => {
      return [
        {
          headerName: "CATEGORY",
          field: "escrowCategory",
          minWidth: 180,
          cellStyle: { paddingTop: "8px", paddingBottom: "8px" },

          // ✅ Make totals row label span across the left side so it won't truncate
          colSpan: (params) => {
            if (params.node.rowPinned) return 5; // CATEGORY .. NEXT DUE DATE(S)
            return 1;
          },
          valueGetter: (params) => {
            if (params.node.rowPinned) return params.data?.escrowCategory || "";
            return params.data?.escrowCategory || "";
          },
          cellRenderer: (params) => {
            const isPinned = params.node.rowPinned;
            const label = params.value || "";
            const note = params.data?.note;

            if (!isPinned) return label;

            return (
              <div style={{ lineHeight: 1.2 }}>
                <div style={{ fontWeight: 800 }}>{label}</div>
                {note ? (
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: green[800],
                      marginTop: 4,
                    }}
                  >
                    {note}
                  </div>
                ) : null}
              </div>
            );
          },
        },
        {
          headerName: "SUB-CATEGORY",
          field: "escrowSubCategory",
          minWidth: 150,
          // spanned over on pinned row
          valueGetter: (p) =>
            p.node.rowPinned ? "" : p.data?.escrowSubCategory,
        },
        {
          field: "months_collected",
          headerName: "MONTHS COLLECTED",
          minWidth: 155,
        },
        {
          field: "totalDueAmount",
          type: "dollar",
          headerName: "TOTAL DUE AMOUNT",
          minWidth: 150,
        },
        {
          headerName: "PAID TO",
          field: "paidTo",
          minWidth: 100,
          flex: 1,
          valueGetter: (p) => (p.node.rowPinned ? "" : p.data?.paidTo),
        },
        {
          headerName: "FREQUENCY",
          field: "dueDateFrequency",
          minWidth: 120,
          width: 120,
          maxWidth: 140,
          valueFormatter: (p) =>
            p.node.rowPinned ? "" : formatFrequency(p.value),
          cellStyle: { color: grey[700] },
        },
        {
          headerName: "NEXT DUE DATE(S)",
          field: "nextDueDate",
          minWidth: 140,
          // minWidth: 170,
          width: 190,
          maxWidth: 220,
          valueFormatter: (p) =>
            p.node.rowPinned ? "" : formatDateShort(p.value),
        },
        {
          headerName: "DUE AMT",
          field: "nextDueAmount",
          type: "dollar",
          minWidth: 100,
          width: 130,
          maxWidth: 140,
          valueFormatter: (p) =>
            p.node.rowPinned ? "" : formatDollar(p.value, ""),
        },
        {
          headerName: "ANNUAL",
          field: "annualAmount",
          type: "dollar",
          minWidth: 110,
          width: 130,
          maxWidth: 140,
          valueFormatter: (p) => formatDollar(p.value, ""),
          cellStyle: (p) => (p.node.rowPinned ? { fontWeight: 800 } : null),
        },
        {
          headerName: "MONTHLY",
          colId: "monthlyAmount",
          minWidth: 110,
          width: 130,
          maxWidth: 140,
          type: "dollar",
          valueGetter: (params) => {
            const { node, data } = params || {};
            if (node.rowPinned) {
              return data?.monthlyAmount ?? 0;
            }
            const annual = parseFloat(data?.annualAmount);
            if (isNaN(annual)) return 0;
            return annual / 12;
          },
          valueFormatter: (p) => formatDollar(p.value, ""),
          cellStyle: (p) => (p.node.rowPinned ? { fontWeight: 800 } : null),
        },
      ];
    }, []);

    const handleOnRowClicked = useCallback(
      (params) => {
        if (params.node.rowPinned) return;
        onRowClicked?.(params?.data);
      },
      [onRowClicked]
    );

    const defaultColDef = useMemo(
      () => ({
        filter: true,
        flex: 1,
        cellClass: "ag-cell-vertical-center",
        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
        resizable: true,
        sortable: true,
        wrapText: true,
        autoHeight: true,
      }),
      []
    );

    const columnTypes = useMemo(
      () => ({
        ...columnTypesDollar(),
      }),
      []
    );

    const onFirstDataRendered = useCallback((params) => {
      agApiSizeColumnsToFit(params);
    }, []);

    const totals = useMemo(() => {
      if (!rowData || !Array.isArray(rowData)) {
        return { annualTotal: 0, monthlyTotal: 0, count: 0 };
        // return { annualTotal: 0, monthlyTotal: 0, count: 0 };
      }
      const annualTotal = rowData.reduce((sum, r) => {
        const v = parseFloat(r?.annualAmount);
        return sum + (isNaN(v) ? 0 : v);
      }, 0);
      const monthlyTotal = annualTotal / 12;
      return { annualTotal, monthlyTotal, count: rowData.length };
    }, [rowData]);

    const pinnedBottomRowData = useMemo(() => {
      if (!showTotals || isEmpty(rowData)) return undefined;
      return [
        {
          escrowCategory: "TOTAL MONTHLY ESCROW",
          annualAmount: totals.annualTotal,
          monthlyAmount: totals.monthlyTotal,
          isPinnedRow: true,
          note: `${totals.count} escrow items • ${formatDollar(
            totals.annualTotal,
            "$0"
          )} annual`,
        },
      ];
    }, [showTotals, totals, rowData]);

    const getRowStyle = useCallback((params) => {
      if (params.node.rowPinned) {
        return {
          fontWeight: "700",
          backgroundColor: green[50],
          color: green[900],
          borderTop: `1px solid ${green[100]}`,
        };
      }
      return null;
    }, []);

    const getRowHeight = useCallback((params) => {
      if (params.node.rowPinned) return 64;
      return 50;
    }, []);

    return (
      <AgGridTableWrapper
        suppressMinHeightWhenPopulated={rowData}
        simpleTable
        sx={{ minWidth: "900px" }}
      >
        <AgGridReact
          ref={ref}
          rowData={rowData}
          loading={loading}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          quickFilterText={quickFilterText}
          tooltipShowDelay={100}
          onFirstDataRendered={onFirstDataRendered}
          columnTypes={columnTypes}
          domLayout={"autoHeight"}
          rowHeight={50}
          getRowHeight={getRowHeight}
          onRowClicked={handleOnRowClicked}
          pinnedBottomRowData={pinnedBottomRowData}
          getRowStyle={getRowStyle}
          suppressRowHoverHighlight={false}
          suppressMovableColumns
        />
      </AgGridTableWrapper>
    );
  }
);

EscrowItemsTable.displayName = "EscrowItemsTable";

export default EscrowItemsTable;
